import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilter, TFilters, TOrderBy } from 'redux/types';
import { getSpecialTerritoryListRequest } from 'redux/reducers/taxDuties/reducer';
import {
	getSpecialTerritoryList,
	getSpecialTerritoryListLoading,
} from 'redux/reducers/taxDuties/selectors';
import { TSpecialTerritory } from 'redux/reducers/taxDuties/types';
import { getCountryListRequest } from 'redux/reducers/country/reducer';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { getUserIsAdmin } from 'redux/reducers/user/selectors';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { SpecialTerritoryDialog } from './SpecialTerritoryDialog';
import { PostalCodesDialog } from './PostalCodesDialog';
import { ETaxDutiesTabGroupLabel } from '../../constants';

const EDIT_ACTION = 'Edit';
const POSTAL_CODES_ACTION = 'Postal Codes';

/** The Country filter column: its picker writes a code, the endpoint wants the id. */
const COUNTRY_ID_FIELD_NAME = 'countryId';

export const EuSpecialVatTerritories: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [territoryToEdit, setTerritoryToEdit] = useState<TSpecialTerritory | null>(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [postalCodesTerritory, setPostalCodesTerritory] = useState<TSpecialTerritory | null>(null);

	const list = useSelector(getSpecialTerritoryList);
	const listLoading = useSelector(getSpecialTerritoryListLoading);
	const countries = useSelector(getCountryListData);
	const userIsAdmin = useSelector(getUserIsAdmin);

	/** The dialog picks a country out of this list, and the table resolves the flags from it. */
	useEffect(() => {
		if (!countries) dispatch(getCountryListRequest());
	}, [countries, dispatch]);

	const params = useMemo(() => queryString.parse(search), [search]);

	const loadList = useCallback(() => {
		const filters = (
			getFiltersFromNavigateParams(defaultTableStructure, params) as TFilters
		).flatMap<TFilter>((filter) => {
			if (filter.fieldName !== COUNTRY_ID_FIELD_NAME) return filter;
			const countryId = countries?.find((item) => item.isoAlpha2 === filter.value)?.id;
			if (!countryId) return [];
			return [{ ...filter, value: String(countryId) }];
		});

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.ASC });

		dispatch(
			getSpecialTerritoryListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [countries, dispatch, params]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TSpecialTerritory>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const handleAction = useCallback(
		(id: number, action: string) => {
			const territory = list?.data?.find((item) => item.id === id) ?? null;
			if (action === EDIT_ACTION) {
				setTerritoryToEdit(territory);
				setEditDialogOpen(true);
			}
			if (action === POSTAL_CODES_ACTION) {
				setPostalCodesTerritory(territory);
			}
		},
		[list],
	);

	const handleAddNew = useCallback(() => {
		setTerritoryToEdit(null);
		setEditDialogOpen(true);
	}, []);

	const handleEditDialogClose = useCallback(() => {
		setEditDialogOpen(false);
		setTerritoryToEdit(null);
	}, []);

	return (
		<>
			<SpecialTerritoryDialog
				open={editDialogOpen}
				onClose={handleEditDialogClose}
				territoryToEdit={territoryToEdit}
			/>
			<PostalCodesDialog
				open={!!postalCodesTerritory}
				onClose={() => setPostalCodesTerritory(null)}
				territory={postalCodesTerritory}
			/>
			<EasTableWithFilter<TSpecialTerritory>
				tableStructure={currentTableStructure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / Tax &amp; Duties /{' '}
						<b>{ETaxDutiesTabGroupLabel.EU_SPECIAL_VAT_TERRITORIES}</b>
					</Typography>
					{userIsAdmin && (
						<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
							Add New Territory
						</Button>
					)}
				</Box>
			</EasTableWithFilter>
		</>
	);
};
